import React, { useState } from 'react';
import { Box, Button, CircularProgress, FormControlLabel, IconButton, Stack, Switch, Tooltip, Typography } from '@mui/material';
import RoomIcon from '@mui/icons-material/Room';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useSystemBySlug } from '../../api/systemBySlug';
import { fetchCurrentSystemIntel } from '../../api/intel';
import { fetchRoster } from '../../api/characters';
import { PresenceEvent, usePresenceStream } from '../../hooks/usePresenceStream';
import { addFavoriteSystem, fetchFavoriteSystems, removeFavoriteSystem } from '../../api/systems';
import { setWaypoint } from '../../api/waypoint';
import { useAuth } from '../../services/AuthContext';
import { useSystemHistory } from '../../hooks/useSystemHistory';
import Chevron from '../../components/Chevron';
import ConsolePanel from '../../components/ConsolePanel';
import CorpActivityDock from '../../components/CorpActivityDock';
import DepositToVaultButton from '../../components/DepositToVaultButton';
import LcarsBlock from '../../components/LcarsBlock';
import LcarsRule from '../../components/LcarsRule';
import ScanFrame from '../../components/ScanFrame';
import StatusBadge from '../../components/StatusBadge';
import SystemSearchBox from '../../components/SystemSearchBox';
import {
  C,
  FONT,
  classColor,
  frameSx,
  hexToRgba,
  igniteSx,
  lcarsLabelSx,
  panelWipeSx,
  prefersReducedMotion,
  revealSx,
} from '../../theme/tokens';
import { SystemIntel } from '../../types';

// Dry goblin flavor for the one "live readout" line under the headline —
// factual framing only (never a claim about the system's actual data), a
// stable pick per system (solar_system_id-keyed) so it doesn't reshuffle
// on every refetch.
const GOBLIN_LINES = [
  'goblin has personally verified this system exists',
  'scanner uplink: nominal. goblin morale: also nominal',
  'no bounty on this rock. yet',
  'certified 100% inside a wormhole',
  'the goblin was here and is judging your fit',
];

const TABS = [
  { label: 'Overview', suffix: '' },
  { label: 'Sites', suffix: '/sites' },
  { label: 'Killboard', suffix: '/killboard' },
];

/** The canonical, shareable page for one system: /system/J224754.
 *
 * Public by design — class, statics, effect, sites, celestials and
 * killboard are all public EVE knowledge plus a public third-party
 * killboard, so an anonymous visitor sees the same intel a logged-in
 * member does. Login only adds the account-specific extras (the corp
 * activity trail, favoriting, set-destination), never the intel itself.
 *
 * This component owns the header + tab strip; each tab renders into the
 * Outlet and reads the already-fetched intel from context, so switching
 * tabs never refetches.
 *
 * `live` (/intel/current) shows the active character's current system instead
 * of the one in the URL. usePresenceSync refetches ['intel'] on every jump, so
 * the page follows the character the same way the dashboard does. */
const SystemPage: React.FC<{ live?: boolean }> = ({ live = false }) => {
  const { slug = '' } = useParams();
  const { me } = useAuth();
  const current = useQuery({
    queryKey: ['intel', 'current-system'],
    queryFn: fetchCurrentSystemIntel,
    enabled: live,
    retry: false,
  });
  const byName = useSystemBySlug(live ? '' : slug);
  const intel = live ? current.data : byName.data;
  const isLoading = live ? current.isLoading : byName.isLoading;
  const base = live ? '/intel/current' : `/system/${encodeURIComponent(slug)}`;
  const { push: pushHistory } = useSystemHistory();

  // Follow: while on, a move by a character that was here when it was switched
  // on asks before this page changes. Kept for this visit only, because a
  // remembered "follow" would switch pages on a later load without asking.
  const [following, setFollowing] = useState<number[] | null>(null);
  const [pendingMove, setPendingMove] = useState<PresenceEvent | null>(null);
  const roster = useQuery({ queryKey: ['characters', 'roster'], queryFn: fetchRoster, enabled: me.isMember });
  const location = useLocation();
  const navigate = useNavigate();
  const thisSystemId = intel?.system.solar_system_id;
  const here = (roster.data ?? []).filter((r) => r.solarSystemId === thisSystemId).map((r) => r.eveCharacterId);

  usePresenceStream((event) => {
    if (!following?.includes(event.eveCharacterId) || event.solarSystemId === thisSystemId) return;
    setPendingMove(event);
  }, me.isMember);

  const moveTo = (event: PresenceEvent) => {
    // Follow carries on with the character that moved, now that the page is theirs.
    setFollowing([event.eveCharacterId]);
    setPendingMove(null);
    // Same tab on the new system: /system/<name>/sites stays /sites.
    navigate(location.pathname.replace(/^\/system\/[^/]+/, `/system/${encodeURIComponent(event.systemName)}`));
  };

  React.useEffect(() => {
    if (intel) pushHistory({ solarSystemId: intel.system.solar_system_id, name: intel.system.name });
  }, [intel, pushHistory]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress sx={{ color: C.blue }} />
      </Box>
    );
  }

  if (!intel) {
    // Live with no location data: say so, instead of calling the current
    // system a name that matches nothing.
    if (live) return <NoLocation />;
    return (
      <ConsolePanel title="No Such System" accent={C.red} corners={false} sx={{ ...frameSx(C.red), mt: 4 }}>
          <Typography sx={{ color: C.textDim, mb: 2 }}>
            Nothing in the registry matches &ldquo;{slug}&rdquo;. Check the spelling — system pages live at{' '}
            <Box component="span" sx={{ fontFamily: FONT.mono, color: C.text }}>
              /system/J224754
            </Box>
            .
          </Typography>
          {/* A mistyped or stale link lands here, so this is exactly where a
              search is most useful — and it must work logged out. */}
        <SystemNotFoundSearch />
      </ConsolePanel>
    );
  }

  return (
    <Stack spacing={3} sx={revealSx()}>
        {/* The console populates rather than dissolves: identity, then the
            tab rail, then the payload, each wiping in behind the last.
            Three beats only — the panels inside the payload do not each get
            their own, or a dense page turns into a slot machine. */}
        <Box sx={panelWipeSx(0)}>
          <SystemHeader intel={intel} isMember={me.isMember} />
        </Box>
        {me.isMember && !live && (
          <FollowToggle
            on={following !== null}
            available={here.length > 0}
            onChange={(on) => {
              setFollowing(on ? here : null);
              setPendingMove(null);
            }}
          />
        )}
        {pendingMove && (
          <SwitchPrompt event={pendingMove} onSwitch={() => moveTo(pendingMove)} onStay={() => setPendingMove(null)} />
        )}
        <Box sx={panelWipeSx(110)}>
          <SystemTabs base={base} />
        </Box>
        {/* Keyed on the system: a jump remounts the tab, so its state (Sites
            paste matches, filters) belongs to the system it was made for. */}
        <Box key={intel.system.solar_system_id} sx={panelWipeSx(220)}>
          <Outlet context={intel satisfies SystemIntel} />
        </Box>
      <SystemPageFooter />
    </Stack>
  );
};

/** Tabs read the intel their parent already fetched — no per-tab request,
 * no prop drilling through the router. */
export const useSystemIntel = () => useOutletContext<SystemIntel>();

/** The URL this page is showing, minus the tab: `/system/<name>` or
 * `/intel/current`. Links made from a tab use it, so a click stays on the page
 * it was made from instead of jumping to a fixed system snapshot. */
export const useSystemBase = () => {
  const { pathname } = useLocation();
  return pathname.replace(/\/(sites|killboard)(\/.*)?$/, '').replace(/\/$/, '');
};

/** Off until one of your characters is in this system to follow. */
const FollowToggle: React.FC<{ on: boolean; available: boolean; onChange: (on: boolean) => void }> = ({
  on,
  available,
  onChange,
}) => (
  <Tooltip
    title={
      available || on
        ? 'Ask before this page moves when a character here jumps somewhere else'
        : 'None of your characters is in this system'
    }
  >
    <Box component="span" sx={{ display: 'inline-flex', alignSelf: 'flex-start' }}>
      <FormControlLabel
        sx={{ ml: 0 }}
        control={
          <Switch size="small" checked={on} disabled={!available && !on} onChange={(e) => onChange(e.target.checked)} />
        }
        label={
          <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.textDim }}>Follow location</Typography>
        }
      />
    </Box>
  </Tooltip>
);

/** The one thing follow mode adds unprompted: a followed character has moved.
 * Stays until answered, same as the dashboard's entry banner. */
const SwitchPrompt: React.FC<{ event: PresenceEvent; onSwitch: () => void; onStay: () => void }> = ({
  event,
  onSwitch,
  onStay,
}) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ ...frameSx(C.cyan), px: 2.5, py: 1.25 }}>
    <Typography sx={{ color: C.text, fontSize: '1rem' }}>
      <Box component="span" sx={{ fontWeight: 700 }}>
        {event.characterName}
      </Box>{' '}
      is now in{' '}
      <Box component="span" sx={{ color: C.cyan, fontFamily: FONT.mono, fontWeight: 700 }}>
        {event.systemName}
      </Box>
    </Typography>
    <Box sx={{ flex: 1 }} />
    <Button size="small" onClick={onSwitch} sx={{ color: C.cyan }}>
      Switch
    </Button>
    <Button size="small" onClick={onStay} sx={{ color: C.textDim }}>
      Stay
    </Button>
  </Stack>
);

/** Live page with no location: the character needs location access before it
 * can say where it is. Search still works, so offer that. */
const NoLocation: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Stack spacing={2} sx={revealSx()}>
      <ConsolePanel title="No Location Data" accent={C.red} corners={false} sx={frameSx(C.red)}>
        <Typography sx={{ color: C.textDim, mb: 2 }}>
          This character needs to (re)link with location access before it can show its current system.
          Search for one instead — every system page carries the same intel.
        </Typography>
        <SystemSearchBox
          onSelect={(sys) => navigate(`/system/${encodeURIComponent(sys.name)}`)}
          placeholder="Search for a system…"
        />
      </ConsolePanel>
    </Stack>
  );
};

const SystemNotFoundSearch: React.FC = () => {
  const navigate = useNavigate();
  return (
    <SystemSearchBox
      onSelect={(sys) => navigate(`/system/${encodeURIComponent(sys.name)}`)}
      placeholder="Search for a system…"
    />
  );
};

const SystemHeader: React.FC<{ intel: SystemIntel; isMember: boolean }> = ({ intel, isMember }) => {
  const { system, online, ship } = intel;
  const queryClient = useQueryClient();

  // classColor() returns C.textDim (an rgba() string, not hex) when the
  // system has no class — the name can use that directly, but the frame's
  // hairline/glow needs a real hex to feed hexToRgba(), so it falls back to
  // the brand accent instead of the unclassified dim.
  const nameColor = classColor(system.system_class);
  const frameColor = nameColor.startsWith('#') ? nameColor : C.blue;
  const goblinLine = GOBLIN_LINES[system.solar_system_id % GOBLIN_LINES.length];

  const favorites = useQuery({ queryKey: ['systems', 'favorites'], queryFn: fetchFavoriteSystems, enabled: isMember });
  const isFavorite = !!favorites.data?.personal.some((f) => f.solar_system_id === system.solar_system_id);

  const toggleFavorite = useMutation({
    mutationFn: () =>
      isFavorite ? removeFavoriteSystem(system.solar_system_id) : addFavoriteSystem(system.solar_system_id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['systems', 'favorites'] }),
  });
  const destination = useMutation({ mutationFn: () => setWaypoint(system.solar_system_id) });

  return (
    <ScanFrame color={frameColor} corners={false} sx={{ ...igniteSx(frameColor), display: 'block' }}>
      <Stack direction="row" alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* The class block is the one solid-fill mark on this page — the
              single fact the whole surface is about, so it earns the fill
              while the 40 site tags below stay outlined. No eyebrow above
              the name: the name itself is the heading. */}
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
            <Typography
              variant="h1"
              sx={{
                color: nameColor,
                fontFamily: FONT.lcars,
                fontWeight: 700,
                letterSpacing: '0.02em',
                fontSize: { xs: '2.2rem', md: '2.8rem' },
                lineHeight: 1,
              }}
            >
              {system.name}
            </Typography>
            {system.system_class && <LcarsBlock label={system.system_class} accent={frameColor} />}
          </Stack>
          <LcarsRule accent={frameColor} sx={{ my: 1.25, maxWidth: 360 }} />
          <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.8rem' }}>
            {system.region_name} &middot; security {system.security_status?.toFixed(2) ?? '—'}
          </Typography>
          <Typography sx={{ color: hexToRgba(frameColor, 0.7), fontFamily: FONT.mono, fontSize: '0.72rem', mt: 0.5 }}>
            &gt; {goblinLine}
          </Typography>
        </Box>

        {/* Account-specific actions only — an anonymous visitor gets the
            same intel, just nothing to act on it with. */}
        {isMember && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CorpActivityDock solarSystemId={system.solar_system_id} systemName={system.name} />
            {/* Filing the system was previously only possible from /vault,
                which meant leaving the dossier you just read and searching
                for the same system again. */}
            <DepositToVaultButton solarSystemId={system.solar_system_id} systemName={system.name} />
            <Tooltip title={isFavorite ? 'Remove star' : 'Star this system'}>
              <IconButton size="small" onClick={() => toggleFavorite.mutate()} sx={{ color: isFavorite ? C.gold : C.textDim }}>
                {isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Set in-game destination">
              <IconButton size="small" onClick={() => destination.mutate()} sx={{ color: C.cyan }}>
                <RoomIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      {(online || ship) && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          {online && (
            <StatusBadge
              label={online.online ? 'Character online' : 'Character offline'}
              accent={online.online ? C.green : C.neutral}
              dim={!online.online}
            />
          )}
          {ship && <StatusBadge label={`${ship.shipName} (${ship.shipTypeName})`} accent={C.neutral} dim />}
        </Stack>
      )}
    </ScanFrame>
  );
};

/** Same tab idiom the retired Intel page used (chevron tick + hairline
 * underline, matching NavBar) — but these switch views of one system
 * instead of switching between systems, and each is a real URL. */
const SystemTabs: React.FC<{ base: string }> = ({ base }) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <ScanFrame color={C.cyan} corners={false} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.25, flexWrap: 'wrap' }}>
      <Stack direction="row" spacing={0.5} sx={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
        {TABS.map((tab) => {
          const path = `${base}${tab.suffix}`;
          // Overview is the index route, so it only matches exactly;
          // the others own everything nested beneath them (a site detail
          // page keeps the Sites tab lit).
          const active = tab.suffix === '' ? location.pathname === path : location.pathname.startsWith(path);
          return (
            <Stack
              key={tab.label}
              direction="row"
              alignItems="center"
              spacing={0.6}
              onClick={() => navigate(path)}
              sx={{
                cursor: 'pointer',
                flexShrink: 0,
                px: 1.5,
                py: 0.6,
                borderBottom: active ? `2px solid ${C.cyan}` : '2px solid transparent',
                transition: 'border-color 0.2s ease',
                '&:hover': { borderBottomColor: active ? C.cyan : C.hairlineBright },
              }}
            >
              {active && (
                <Chevron
                  color={C.cyan}
                  size={7}
                  sx={{
                    animation: prefersReducedMotion() ? 'none' : 'tabLock 0.25s cubic-bezier(0.16,1,0.3,1) both',
                    '@keyframes tabLock': {
                      '0%': { transform: 'scale(0.3)', opacity: 0 },
                      '100%': { transform: 'scale(1)', opacity: 1 },
                    },
                  }}
                />
              )}
              <Typography
                sx={{
                  ...lcarsLabelSx(active ? C.textBright : C.textDim, '0.9rem'),
                  whiteSpace: 'nowrap',
                  transition: 'color 0.2s ease',
                }}
              >
                {tab.label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>

      {/* Deliberately here and not only on the Dashboard: the Dashboard sits
          behind the login gate, so without this an anonymous visitor who
          arrived on a shared link would have no way to look up any other
          system. Search is public because the data is. */}
      <SystemSearchBox
        onSelect={(sys) => navigate(`/system/${encodeURIComponent(sys.name)}`)}
        // Full line on a phone — at 220px fixed it sat on top of the tab
        // labels instead of wrapping under them.
        width={{ xs: '100%', sm: 220 }}
        placeholder="Look up another system…"
      />
    </ScanFrame>
  );
};

/** Says where the numbers came from. A stranger following a link has no
 * other way to judge whether any of this is trustworthy. */
const SystemPageFooter: React.FC = () => (
  <Box sx={{ pt: 1 }}>
    <LcarsRule accent={C.neutral} sx={{ mb: 1.25, opacity: 0.5 }} />
    <Typography sx={{ color: hexToRgba(C.neutral, 0.8), fontFamily: FONT.mono, fontSize: '0.66rem' }}>
      Static reference data (class, effects, site catalogue, celestials) from the EVE SDE and the EVE University wiki
      &middot; kill activity live from zKillboard.
    </Typography>
  </Box>
);

export default SystemPage;
